import { defineStore, storeToRefs } from 'pinia'
import * as Sentry from '@sentry/vue'
import type { AxiosResponse } from 'axios'
import type { PasswordRule } from '@/composables/useValidatePassword'
import LOCAL_STORAGE_KEYS from '@constants/localStorageKeys'
import USD_COUNTRIES_ISO_CODES from '@constants/usdCurrencies'
import { client } from '@/services/client'
import { handleRequest } from '@/services/handleRequest'
import { updateAuthToken } from '@/services/interceptors'
import { useSpace } from '@/store/modules/App/space'
import type {
  User,
  UserEnrichedInfoResponse,
  Token,
  PersonalAccessToken,
  AnyToken,
} from '@/types'
import type {
  GenerateLeadRequest,
  GenerateEnterpriseProspect,
} from '@/types/lead'
import { changeLanguageMessages, changeLocale } from '@utils/helpers/translate'
import { isEmpty } from '@/utils/checkers'
import { connectUnleashUser } from '@/utils/setup-application/createUnleash'
import type { Credentials } from '@/components/Auth/Signup/types'
import type { SbTheme } from '@/components/ContentManager/AccountSettings/types'
import { usePartnerUser } from './../Partner/user'
import router from '@/router'

type SignUpPayload = {
  confirm_password: string
  rid?: string
  user: Credentials
}

type SignInPayload = {
  email: string
  otp_attempt: string | null
  password: string
  rid: string
  [key: string]: unknown
}

type AttemptResponse = {
  access_token: string
  email: string
  has_partner: boolean
  redirect: unknown | null
  role: unknown | null
  token_type: string
  user_id: number
}

type AttemptOtpResponse = {
  configured_2fa_options: string[]
  login_strategy: string
  otp_required: boolean
}

type RequestApprovalResponse = {
  approval: {
    id: number
    status: string
  }
}

export const isAttemptOtpResponse = (
  response: AttemptResponse | AttemptOtpResponse,
): response is AttemptOtpResponse => 'otp_required' in response

export type State = {
  // @todo: this should be nullable
  user: User
  access_tokens: AnyToken[]
  currency: 'usd' | 'eur'
  language: string
  languageLoaded: string
  inEu: boolean
  isUserLoggedIn: boolean
  loadingUserPromise: Promise<void> | null
  userEnrichedInfo: UserEnrichedInfoResponse | null
  themePreview: SbTheme | null
}

const state = (): State => ({
  // @todo: this should be instantiated as null
  user: {} as User,
  access_tokens: [],
  currency: 'usd',
  language: 'en',
  languageLoaded: 'en',
  inEu: true,
  isUserLoggedIn: false,
  loadingUserPromise: null,
  userEnrichedInfo: null,
  themePreview: null,
})

export type UserStore = ReturnType<typeof useUser>

export const useUser = defineStore('user', {
  state,
  getters: {
    getUserId: (state: State): number => state.user.id,
    /**
     * @deprecated use `state.user` directly instead
     */
    getUserInfo: (state: State): User => state.user || {},
    hasUser: (state: State): boolean => state.user && !isEmpty(state.user),
    /**
     * @deprecated use `state.language` directly instead
     */
    getUserLanguage: (state: State): string => state.language,
    getUserCurrency: (state: State): 'usd' | 'eur' => state.currency,
    getUserNotified: (state: State): string[] => state.user.notified || [],
    getUserHasPartner: (state: State): boolean => state.user?.has_partner,
    hasUserNotifiedValue:
      (state: State) =>
      (value: string): boolean => {
        return state.user.notified && state.user.notified.indexOf(value) !== -1
      },
    hasUserDisclaimerIdFeatureValue:
      (state: State) =>
      (id: number): boolean =>
        state.user?.disclaimer_ids?.includes(id) ?? false,
    findUserNotifiedValues:
      (state: State) =>
      (value: string): string[] => {
        return state.user?.notified
          ? state.user.notified.filter((item) => item.includes(value))
          : []
      },

    /**
     * @deprecated use `state.isUserLoggedIn` directly instead
     */
    getIsUserLoggedIn: (state: State): boolean => state.isUserLoggedIn,
    getUserTheme: (state: State): SbTheme | undefined =>
      state.themePreview ?? state.user?.ui_theme?.theme,
  },
  actions: {
    setUserInfo(value: User): void {
      this.user = value
      this.language = value?.lang || 'en'
      changeLocale(this.language)
    },

    setUserLanguageLoaded(value: string): void {
      this.languageLoaded = value
    },

    setAccessTokens(value: AnyToken[]): void {
      this.access_tokens = value
    },

    clearUserInfo(): void {
      this.user = {} as User
      changeLocale('en')
    },

    setUserCurrency(value: 'usd' | 'eur'): void {
      this.currency = value
    },

    setUserRegion(value: boolean): void {
      this.inEu = value
    },

    setIsUserLoggedIn(value: boolean): void {
      this.isUserLoggedIn = value
    },

    setUserTrackStatistics(value: boolean): void {
      this.user.track_statistics = value
    },

    setUserEnrichedInfo(enrichedUser: UserEnrichedInfoResponse): void {
      this.userEnrichedInfo = enrichedUser
    },

    setUserThemePreview(theme: SbTheme | null = null): void {
      this.themePreview = theme
    },

    async attemptSignUp({
      payload,
      region,
    }: {
      payload: SignUpPayload
      region: string | null
    }) {
      const response: AxiosResponse<AttemptResponse> = await client.post(
        '/users/signup',
        payload,
        {
          region,
        },
      )

      updateAuthToken(response.data.access_token)
      await this.loadUserInfo()

      return response.data
    },

    async attemptLogin({
      payload,
      region,
    }: {
      payload: SignInPayload
      region: string | null
    }): Promise<AttemptResponse | AttemptOtpResponse> {
      const response: AxiosResponse<AttemptResponse | AttemptOtpResponse> =
        await client.post('/users/login', payload, {
          headers: {
            'x-requested-with': 'XMLHttpRequest',
          },
          region,
        })

      const { data } = response

      if (!isAttemptOtpResponse(data)) {
        updateAuthToken(data.access_token)
        await this.loadUserInfo()
      }

      return data
    },

    async getZendeskToken(): Promise<string> {
      try {
        const { data } = await client.get('/livechat_auth')
        return data.token
      } catch (error) {
        return Promise.reject(error)
      }
    },

    attemptLogout(): Promise<boolean> {
      return new Promise((resolve) => {
        this.clearUserInfo()
        localStorage.removeItem(
          LOCAL_STORAGE_KEYS.third_party_auth_route_to_redirect,
        )
        this.setIsUserLoggedIn(false)
        resolve(true)
      })
    },

    async resendConfirmationEmail(payload: { email: string }): Promise<void> {
      await client.post(`/users/resend_confirm`, payload)
    },

    async sendForgotPasswordEmail(payload: { email: string }): Promise<void> {
      await client.post(`/users/reset_password`, payload)
    },

    async setUserPassword(payload: {
      password_confirmation: string
      password: string
      token: string
    }): Promise<void> {
      await client.post(`/users/set_password`, payload)
    },

    async confirmUserAccount(payload: { token: string }) {
      const response: AxiosResponse<{ reset_token: string | null }> =
        await client.post(`/users/confirm`, payload)
      return response.data
    },

    async attemptSSOLogin(payload: {
      token: string
      discard: boolean
    }): Promise<User> {
      const res: AxiosResponse<{
        access_token: string
      }> = await client.post('/sso', payload)

      updateAuthToken(res.data.access_token)

      const response = await this.loadUserInfo()
      if (response && response.status === 'success' && response.data) {
        return response.data.user
      }
      throw new Error('SSO login failed')
    },

    async getSupportCenterToken(): Promise<string> {
      const { data } = await client.get('/helpcenter_auth')
      return data.token
    },

    async getCustomPasswordComplexity(token: string): Promise<PasswordRule> {
      const { data } = await client.get(
        `/users/${token}/custom_password_complexity`,
      )
      return data
    },

    async loadUserInfo() {
      if (this.loadingUserPromise) {
        return this.loadingUserPromise
      }

      const response = await handleRequest({
        apiCall: (): Promise<AxiosResponse<{ user: User }>> =>
          client.get('users/me'),
        expectedErrors: new Map([[401, '']]),
      })

      if (response.status === 'success') {
        const { user } = response.data

        this.setUserInfo(user)

        this.setupUserHelpers(user)
        this.loadLanguageTranslation()

        if (user.partner_role && user.has_partner) {
          const { partner } = storeToRefs(usePartnerUser())
          const { loadPartnerInfo } = usePartnerUser()

          if (!partner.value) {
            loadPartnerInfo()
          }
        }
      } else {
        if (response.statusCode === 401) {
          router.push({ name: 'Logout' })
        }
      }

      return response
    },

    async loadUserAccessTokens() {
      const response: AxiosResponse<{ tokens: Token[] }> =
        await client.get(`/tokens`)

      if (response.data.tokens) {
        this.setAccessTokens(response.data.tokens)
      }

      return response.data
    },

    async loadLanguageTranslation(reload = false): Promise<void> {
      const language = this.user.lang || 'en'

      if (!reload && this.languageLoaded === language) {
        return
      }

      try {
        const response = await fetch(
          `https://lang.storyblok.com/v2/${language}.json`,
        )
        const jsonFile = await response.json()
        changeLanguageMessages(language, jsonFile)
        this.setUserLanguageLoaded(language)
      } catch (err) {
        console.error(err)
      }
    },

    async updateUserInfo(payload: {
      user: Partial<User>
      otp_attempt?: string
    }): Promise<User> {
      const changedLanguage = payload.user.lang !== this.language

      const response = await client
        .put('/users/me', {
          user: payload.user,
          otp_attempt: payload.otp_attempt,
        })
        .then(({ data }) => {
          const { user } = data

          if (user) {
            this.setUserInfo(user)
          }

          if (changedLanguage) {
            this.loadLanguageTranslation(true)
          }
          return user
        })
        .catch((err) => Promise.reject(err))

      return response
    },

    setupUserHelpers(user: User): void {
      Sentry.setUser({ id: user.id, email: user.email })
      connectUnleashUser(user)
    },

    async generateLead(payload: GenerateLeadRequest): Promise<void> {
      await client.post('users/generate_lead?id=me', payload)
    },

    async generateEnterpriseProspect(
      payload: GenerateEnterpriseProspect,
    ): Promise<void> {
      await client.post('users/generate_enterprise_prospect', payload)
    },

    async loadUserCurrency(): Promise<void> {
      const { data } = await client.get(`countries/me`)
      const isUsdCountry = USD_COUNTRIES_ISO_CODES.find(
        (isoCode) => isoCode === data.country,
      )

      if (!isUsdCountry) {
        this.setUserCurrency('eur')
      }

      this.setUserRegion(data.country !== 'US')
    },

    async addFeatureDisclaimerId(id: number): Promise<User | void> {
      if (this.hasUserDisclaimerIdFeatureValue(id)) {
        console.warn(`Disclaimer id ${id} already added to the user.`)
        return
      }
      const userData = await this.updateUserInfo({
        user: {
          disclaimer_ids: this.user.disclaimer_ids.concat(id),
        },
      })

      return userData
    },

    async addUserNotifiedValue(value: string | string[]): Promise<User> {
      const uniqueValues = new Set([
        ...(this.user.notified || []),
        ...(Array.isArray(value) ? value : [value]),
      ])
      const userData = await this.updateUserInfo({
        user: {
          ...this.user,
          notified: Array.from(uniqueValues),
        },
      })
      return userData
    },

    async removeUserNotifiedValue(value: string): Promise<User> {
      const filteredValues =
        this.user.notified?.filter((item) => item !== value) || []
      const userData = await this.updateUserInfo({
        user: {
          ...this.user,
          notified: filteredValues,
        },
      })
      return userData
    },

    toggleUserNotifiedValue(value: string): void {
      if (!this.user?.notified) {
        return
      }

      if (this.user.notified?.includes(value)) {
        this.removeUserNotifiedValue(value)
      } else {
        this.addUserNotifiedValue(value)
      }
    },

    async checkUserLoggedIn(): Promise<void> {
      try {
        const response: AxiosResponse<{ user: User }> =
          await client.get('users/me')

        this.setIsUserLoggedIn(Boolean(response.data?.user))
      } catch (error) {
        console.error(error)
        this.setIsUserLoggedIn(false)
      }
    },

    async requestApproval({
      spaceId,
      approval,
    }: {
      spaceId: number
      approval: {
        approver_id: number
        release_id: number
      }
    }): Promise<RequestApprovalResponse> {
      const response: AxiosResponse<RequestApprovalResponse> =
        await client.post(
          `spaces/${spaceId}/approvals/`,
          { approval },
          { region: useSpace().region },
        )
      return response.data
    },

    async changeUserTrackStatistics(user: User): Promise<User> {
      const response = await client.put('/users/me', { user })

      this.setUserTrackStatistics(response.data.user.track_statistics)

      return response.data.user
    },

    async removeAvatar(): Promise<void> {
      await client.delete('/users/remove_avatar')
    },

    async loadUserEnrichedInfo(email: string) {
      const response: AxiosResponse<UserEnrichedInfoResponse> =
        await client.get(`fetch_users_info?email=${email}`)
      this.setUserEnrichedInfo(response.data)

      return response.data
    },

    async editTokenName(
      tokenId: Token['id'],
      newTokenName: Token['name'],
    ): Promise<void> {
      await client.put(`/tokens/${tokenId}`, {
        name: newTokenName ?? '',
      })

      const token = this.access_tokens.find((t) => t.id === tokenId)

      if (token) {
        token.name = newTokenName
      }
    },

    async createUserToken(payload: {
      name: string | null
      expire?: string
    }): Promise<Token> {
      const { data } = await client.post(`/tokens`, payload)
      return data
    },

    async deleteUserToken(tokenId: number): Promise<void> {
      await client.delete(`/tokens/${tokenId}`)
    },

    async loadPersonalAccessTokens() {
      // this API also retrieves legacy user tokens
      const response: AxiosResponse<{
        personal_access_tokens: PersonalAccessToken[]
      }> = await client.get('/personal_access_tokens')

      if (response.data.personal_access_tokens) {
        this.setAccessTokens(response.data.personal_access_tokens)
      }
    },

    async createPersonalAccessToken(payload: {
      name: string | null
      expires_at: string
      scopes: string[]
      space_ids: number[]
    }): Promise<PersonalAccessToken> {
      const response: AxiosResponse<PersonalAccessToken> = await client.post(
        '/personal_access_tokens',
        payload,
      )

      return response.data
    },

    async editPersonalAccessTokenName(tokenId: number, newTokenName: string) {
      await client.put(`/personal_access_tokens/${tokenId}`, {
        name: newTokenName,
      })

      const token = this.access_tokens.find((t) => t.id === tokenId)

      if (token) {
        token.name = newTokenName
      }
    },

    async deletePersonalAccessToken(tokenId: number): Promise<void> {
      await client.delete(`/personal_access_tokens/${tokenId}`)
    },
  },
})
